import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Mail, Users, RefreshCcw, AlertTriangle } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";

const MAX_INVITES = 25;

const SoftLaunchInviteCard = () => {
  const [emails, setEmails] = useState("");
  const [senderName, setSenderName] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], stats: null });

  const load = async () => {
    try {
      const d = await apiGet("/admin/soft-launch/invitations?limit=100");
      setData(d);
    } catch (e) {
      toast.error("Could not load invitations");
    }
  };
  useEffect(() => { load(); }, []);

  const parsedEmails = emails
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.includes("@") && e.length >= 5);
  const tooMany = parsedEmails.length > MAX_INVITES;

  const send = async () => {
    if (parsedEmails.length === 0) {
      toast.error("Add at least one email address");
      return;
    }
    if (tooMany) {
      toast.error(`Batch limited to ${MAX_INVITES} addresses`);
      return;
    }
    try {
      setLoading(true);
      const res = await apiPost("/admin/soft-launch/send", {
        emails: parsedEmails,
        sender_name: senderName || undefined,
        custom_message: customMessage || undefined,
      });
      toast.success(
        `Invitations: ${res.sent} sent / ${res.failed} failed${res.dev_mode ? " (dev mode — emails logged)" : ""}`
      );
      setEmails("");
      setCustomMessage("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  const stats = data?.stats;
  return (
    <Card data-testid="soft-launch-invite-card" className="border">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 font-display text-base">
            <Mail className="h-4 w-4 text-primary" /> Soft launch invitations
          </CardTitle>
          <CardDescription>
            Send the personalised invitation email to your first 10–20 invitees and track who signs up.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="soft-launch-refresh">
          <RefreshCcw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="soft-launch-stats">
            <Stat label="Invites sent" value={stats.invites_sent} />
            <Stat label="Invites failed" value={stats.invites_failed} tone={stats.invites_failed > 0 ? "warn" : null} />
            <Stat label="Users signed up" value={stats.users_signed_up} />
            <Stat label="Conversion" value={`${stats.conversion_pct}%`} />
          </div>
        )}

        {/* Form */}
        <div className="space-y-2">
          <label className="block">
            <span className="text-xs text-muted-foreground">
              Email addresses (comma, space or newline separated · max {MAX_INVITES})
            </span>
            <Textarea
              data-testid="soft-launch-emails"
              placeholder="alice@example.com, bob@example.com"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              rows={3}
            />
            <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">
                {parsedEmails.length}/{MAX_INVITES}
              </Badge>
              {tooMany && (
                <span className="text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Too many — will be rejected
                </span>
              )}
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Sender name (defaults to admin name)</span>
            <Input
              data-testid="soft-launch-sender"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="e.g. Tharindu (CSE Investor founder)"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Custom intro message (optional)</span>
            <Textarea
              data-testid="soft-launch-custom"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="e.g. ‘Saw you tweet about CSE last week — thought you'd find this useful.’"
              rows={2}
            />
          </label>
          <Button onClick={send} disabled={loading || parsedEmails.length === 0 || tooMany} data-testid="soft-launch-send">
            <Send className="h-4 w-4" /> {loading ? "Sending…" : `Send ${parsedEmails.length} invitation${parsedEmails.length === 1 ? "" : "s"}`}
          </Button>
        </div>

        {/* History */}
        <div data-testid="soft-launch-history">
          <div className="text-xs font-semibold mb-2 flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Recent invitations
          </div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-border">
            {(data.items || []).length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">No invitations sent yet.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr className="text-left">
                    <th className="p-2 font-medium">Email</th>
                    <th className="p-2 font-medium">Sent</th>
                    <th className="p-2 font-medium">Status</th>
                    <th className="p-2 font-medium">Signed up?</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.items || []).slice(0, 50).map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="p-2 truncate max-w-[180px]">{r.email}</td>
                      <td className="p-2 text-muted-foreground whitespace-nowrap">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                      </td>
                      <td className="p-2">
                        {r.ok ? (
                          <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400">
                            Sent
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-destructive/40 text-destructive">
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">
                        {r.user_signed_up ? (
                          <Badge className="bg-primary/15 text-primary border-primary/30">
                            ✅ {r.user_tier || "signed up"}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-md border border-border bg-muted/20 px-3 py-2 ${tone === "warn" ? "border-[hsl(var(--warning))]/40" : ""}`}>
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="text-base font-semibold">{value}</div>
  </div>
);

export default SoftLaunchInviteCard;
